import React, { useState, useEffect } from "react";
import {
  Typography,
  Table,
  Space,
  Button,
  Modal,
  Form,
  Input,
  DatePicker,
  Select,
  Checkbox,
  message,
  Breadcrumb,
  Tag,
  Tooltip,
  Badge,
  Row,
  Col,
  Card,
} from "antd";
import { Link, useNavigate } from "react-router-dom";
import {
  HomeOutlined,
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  SearchOutlined,
  UserOutlined,
} from "@ant-design/icons";
import { supabase } from "../lib/supabase";
import dayjs, { Dayjs } from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import { trackFeatureUsage } from "../lib/posthog";
import { AdminNavBar } from "../components/AdminNavBar";
import { useUser } from "../hooks/useUser";

const { Title, Text } = Typography;
const { TextArea } = Input;
const { Option } = Select;
const { RangePicker } = DatePicker;

// Add the relative time plugin to dayjs
dayjs.extend(relativeTime);

interface Task {
  id: number;
  created_at: string;
  booking_id: number | null;
  company_id: number | null;
  date: string | null;
  name: string | null;
  description: string | null;
  completed: boolean | null;
  admin_user_id: string | null;
  completed_on: string | null;

  booking?: {
    id: number;
    reference: string;
    transport_type?: string;
    transport_types?: {
      abbreviation: string;
    };
  };
  company?: {
    id: number;
    name: string;
  };
}

interface AdminUserOption {
  user_id: string;
  name: string | null;
  email?: string;
}

interface Company {
  id: number;
  name: string;
}

interface Booking {
  id: number;
  reference: string;
  transport_type: string;
  company_name?: string;
  transport_types?: {
    abbreviation: string;
  };
}

interface FilterState {
  adminUserId: string | undefined;
  completed: boolean | undefined;
  date: [Dayjs, Dayjs] | undefined;
  search: string;
  bookingId: number | undefined;
  companyId: number | undefined;
}

// Helper function to get abbreviation of transport type
const getTransportTypeAbbr = (
  booking?: { transport_types?: { abbreviation: string } } | null
): string => {
  if (!booking || !booking.transport_types) return "";

  try {
    return booking.transport_types.abbreviation || "";
  } catch (_err) {
    console.error("Error processing transport type abbreviation:", booking);
    return "";
  }
};

// Helper function to format booking ID with abbreviation
const formatBookingId = (
  booking?: { id: number; transport_types?: { abbreviation: string } } | null
): string => {
  if (!booking) return "";
  const abbr = getTransportTypeAbbr(booking);
  return `EFF${booking.id}${abbr}`;
};

const TasksAdmin = () => {
  const navigate = useNavigate();
  const { user } = useUser();
  const [form] = Form.useForm();
  const [tasks, setTasks] = useState<Task[]>([]);
  const [adminUsers, setAdminUsers] = useState<AdminUserOption[]>([]);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [bookings, setBookings] = useState<Booking[]>([]);
  const [loading, setLoading] = useState(true);
  const [modalVisible, setModalVisible] = useState(false);
  const [modalTitle, setModalTitle] = useState("Add Task");
  const [editingTask, setEditingTask] = useState<Task | null>(null);
  const [filters, setFilters] = useState<FilterState>({
    adminUserId: undefined,
    completed: undefined,
    date: undefined,
    search: "",
    bookingId: undefined,
    companyId: undefined,
  });

  const [stats, setStats] = useState({
    total: 0,
    completed: 0,
    pending: 0,
    overdue: 0,
    todayDue: 0,
  });

  const fetchTasks = async () => {
    try {
      setLoading(true);
      let query = supabase
        .from("tasks")
        .select(
          `
          *,
          booking:booking_id (
            id, 
            reference, 
            transport_type,
            transport_types:transport_type (abbreviation)
          ),
          company:company_id (id, name)
        `
        )
        .order("created_at", { ascending: false });

      if (filters.adminUserId) {
        query = query.eq("admin_user_id", filters.adminUserId);
      }

      if (filters.completed !== undefined) {
        query = query.eq("completed", filters.completed);
      }

      if (filters.bookingId) {
        query = query.eq("booking_id", filters.bookingId);
      }

      if (filters.companyId) {
        query = query.eq("company_id", filters.companyId);
      }

      if (filters.date && filters.date.length === 2) {
        query = query
          .gte("date", filters.date[0].startOf("day").toISOString())
          .lte("date", filters.date[1].endOf("day").toISOString());
      }

      if (filters.search) {
        query = query.or(
          `name.ilike.%${filters.search}%,description.ilike.%${filters.search}%`
        );
      }

      const { data, error } = await query;

      if (error) {
        throw error;
      }

      if (data) {
        setTasks(data);

        // Calculate statistics
        const now = dayjs();
        const today = now.startOf("day");
        const endOfDay = now.endOf("day");

        const total = data.length;
        const completed = data.filter((task) => task.completed).length;
        const pending = total - completed;
        const overdue = data.filter(
          (task) =>
            !task.completed && task.date && dayjs(task.date).isBefore(now)
        ).length;
        const todayDue = data.filter(
          (task) =>
            !task.completed &&
            task.date &&
            dayjs(task.date).isAfter(today) &&
            dayjs(task.date).isBefore(endOfDay)
        ).length;

        setStats({
          total,
          completed,
          pending,
          overdue,
          todayDue,
        });
      } else {
        setTasks([]);
        setStats({
          total: 0,
          completed: 0,
          pending: 0,
          overdue: 0,
          todayDue: 0,
        });
      }
    } catch (error) {
      console.error("Error fetching tasks:", error);
      message.error("Failed to load tasks");
    } finally {
      setLoading(false);
    }
  };

  const fetchAdminUsers = async () => {
    try {
      const { data, error } = await supabase
        .from("admin_users")
        .select("user_id, name")
        .order("name");

      if (error) throw error;
      const admins = (data ?? []) as AdminUserOption[];
      admins.sort((a, b) =>
        (a.name || "").localeCompare(b.name || "")
      );
      setAdminUsers(admins);
    } catch (error) {
      console.error("Error fetching admin users:", error);
      message.error("Failed to load admin users");
    }
  };

  const fetchCompanies = async () => {
    try {
      const { data, error } = await supabase
        .from("company")
        .select("id, name")
        .order("name");

      if (error) {
        throw error;
      }

      if (data) {
        setCompanies(data);
      } else {
        setCompanies([]);
      }
    } catch (error) {
      console.error("Error fetching companies:", error);
      message.error("Failed to load companies");
    }
  };

  const fetchBookings = async (companyId?: number) => {
    try {
      let query = supabase
        .from("bookings")
        .select(
          `
          id, 
          reference,
          transport_type,
          transport_types:transport_type (abbreviation),
          company:company_id (id, name)
        `
        )
        .order("created_at", { ascending: false })
        .limit(100); // Limit to recent bookings

      // Filter by company if provided
      if (companyId) {
        query = query.eq("company_id", companyId);
      }

      const { data, error } = await query;

      if (error) {
        throw error;
      }

      if (data) {
        // Transform the data to include company_name and transport_types
        const formattedBookings = data.map((booking: any) => ({
          id: booking.id,
          reference: booking.reference,
          transport_type: booking.transport_type,
          transport_types: booking.transport_types,
          company_name: booking.company?.name || null,
        }));
        setBookings(formattedBookings);
      } else {
        setBookings([]);
      }
    } catch (error) {
      console.error("Error fetching bookings:", error);
      message.error("Failed to load bookings");
    }
  };

  useEffect(() => {
    trackFeatureUsage("admin_tasks");
    fetchTasks();
    fetchAdminUsers();
    fetchCompanies();
    fetchBookings();
  }, []);

  useEffect(() => {
    fetchTasks();
  }, [filters]);

  const handleFormSubmit = async (values: Record<string, unknown>) => {
    try {
      const taskData = {
        booking_id: values.booking_id || null,
        company_id: values.company_id || null,
        date: values.date ? (values.date as Dayjs).toISOString() : null,
        name: values.name as string,
        description: values.description as string | null,
        admin_user_id: values.admin_user_id || null,
        completed: (values.completed as boolean) || false,
        completed_on: values.completed ? dayjs().toISOString() : null,
      };

      let result;

      if (editingTask) {
        // Update existing task
        result = await supabase
          .from("tasks")
          .update(taskData)
          .eq("id", editingTask.id);

        if (result.error) throw result.error;
        message.success("Task updated successfully");
      } else {
        // Insert new task
        result = await supabase.from("tasks").insert(taskData);

        if (result.error) throw result.error;
        message.success("Task created successfully");
      }

      // Reset and refetch
      setModalVisible(false);
      form.resetFields();
      setEditingTask(null);
      fetchTasks();
    } catch (error) {
      console.error("Error saving task:", error);
      message.error("Failed to save task");
    }
  };

  const handleEditTask = (task: Task) => {
    setEditingTask(task);
    setModalTitle("Edit Task");

    if (task.company_id) {
      fetchBookings(task.company_id);
    }

    form.setFieldsValue({
      name: task.name,
      description: task.description,
      company_id: task.company_id,
      booking_id: task.booking_id,
      admin_user_id: task.admin_user_id,
      date: task.date ? dayjs(task.date) : null,
      completed: task.completed,
    });

    setModalVisible(true);
  };

  const handleDeleteTask = async (taskId: number) => {
    try {
      Modal.confirm({
        title: "Confirm Deletion",
        content: "Are you sure you want to delete this task?",
        onOk: async () => {
          const { error } = await supabase
            .from("tasks")
            .delete()
            .eq("id", taskId);

          if (error) throw error;

          message.success("Task deleted successfully");
          fetchTasks();
        },
      });
    } catch (error) {
      console.error("Error deleting task:", error);
      message.error("Failed to delete task");
    }
  };

  const adminUserMap = new Map(
    adminUsers.map((a) => [
      a.user_id,
      a.name || "—",
    ])
  );

  const handleMarkComplete = async (task: Task) => {
    try {
      const { error } = await supabase
        .from("tasks")
        .update({
          completed: true,
          completed_on: dayjs().toISOString(),
        })
        .eq("id", task.id);

      if (error) throw error;

      message.success("Task marked as complete");
      fetchTasks();
    } catch (error) {
      console.error("Error updating task:", error);
      message.error("Failed to update task");
    }
  };

  const resetFilters = () => {
    setFilters({
      adminUserId: undefined,
      completed: undefined,
      date: undefined,
      search: "",
      bookingId: undefined,
      companyId: undefined,
    });
  };

  const columns = [
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      render: (text: string, record: Task) => (
        <div>
          <Text strong>{text}</Text>
          {record.company && record.company_id && (
            <div>
              <Tooltip title="View company">
                <Link to={`/admin/companies?id=${record.company_id}`}>
                  <Tag color="purple">Company: {record.company.name}</Tag>
                </Link>
              </Tooltip>
            </div>
          )}
        </div>
      ),
    },
    {
      title: "Booking",
      dataIndex: "booking_id",
      key: "booking_id",
      render: (_: unknown, record: Task) => {
        if (!record.booking || !record.booking_id) return "-";
        return (
          <Tooltip title="View booking">
            <Link to={`/admin/bookings?booking_id=${record.booking_id}`}>
              <Tag color="blue">{formatBookingId(record.booking)}</Tag>
            </Link>
          </Tooltip>
        );
      },
    },
    {
      title: "Assigned To",
      dataIndex: "admin_user_id",
      key: "admin_user_id",
      render: (adminUserId: string | null) => {
        if (!adminUserId) return "Unassigned";
        return adminUserMap.get(adminUserId) ?? "Unassigned";
      },
    },
    {
      title: "Due Date",
      dataIndex: "date",
      key: "date",
      render: (date: string) => {
        if (!date) return "No deadline";

        const dueDate = dayjs(date);
        const isOverdue = !dayjs(date).isAfter(dayjs()) && dueDate.isValid();

        return (
          <div>
            <div>{dueDate.format("MMM D, YYYY")}</div>
            {isOverdue && <Tag color="red">Overdue</Tag>}
          </div>
        );
      },
    },
    {
      title: "Status",
      dataIndex: "completed",
      key: "completed",
      render: (completed: boolean) => {
        return completed ? (
          <Tag color="green" icon={<CheckCircleOutlined />}>
            Completed
          </Tag>
        ) : (
          <Tag color="orange" icon={<ClockCircleOutlined />}>
            Pending
          </Tag>
        );
      },
    },
    {
      title: "Actions",
      key: "actions",
      render: (_: React.Key, record: Task) => (
        <Space>
          {!record.completed && (
            <Tooltip title="Mark Complete">
              <Button
                type="primary"
                size="small"
                icon={<CheckCircleOutlined />}
                onClick={() => handleMarkComplete(record)}
              />
            </Tooltip>
          )}
          <Tooltip title="Edit">
            <Button
              type="default"
              size="small"
              icon={<EditOutlined />}
              onClick={() => handleEditTask(record)}
            />
          </Tooltip>
          <Tooltip title="Delete">
            <Button
              danger
              size="small"
              icon={<DeleteOutlined />}
              onClick={() => handleDeleteTask(record.id)}
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <AdminNavBar />

      <Title level={2}>Tasks Management</Title>

      {/* Task Statistics Cards */}
      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={12} md={8} lg={4}>
          <Card>
            <Statistic title="Total Tasks" value={stats.total} />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={8} lg={4}>
          <Card>
            <Statistic
              title="Completed"
              value={stats.completed}
              valueStyle={{ color: "#52c41a" }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={8} lg={4}>
          <Card>
            <Statistic
              title="Pending"
              value={stats.pending}
              valueStyle={{ color: "#1890ff" }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={8} lg={4}>
          <Card>
            <Statistic
              title="Overdue"
              value={stats.overdue}
              valueStyle={{ color: "#ff4d4f" }}
              prefix={stats.overdue > 0 ? <Badge status="error" /> : null}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={8} lg={4}>
          <Card>
            <Statistic
              title="Due Today"
              value={stats.todayDue}
              valueStyle={{ color: "#fa8c16" }}
              prefix={stats.todayDue > 0 ? <Badge status="warning" /> : null}
            />
          </Card>
        </Col>
      </Row>

      {/* Filters */}
      <div style={{ marginBottom: 16 }}>
        <Row gutter={16}>
          <Col xs={24} md={4}>
            <Input
              placeholder="Search tasks"
              prefix={<SearchOutlined />}
              value={filters.search}
              onChange={(e) =>
                setFilters({ ...filters, search: e.target.value })
              }
              style={{ marginBottom: 8 }}
            />
          </Col>
          <Col xs={24} md={4}>
            <Input
              placeholder="Booking ID"
              type="number"
              value={filters.bookingId}
              onChange={(e) => {
                const value = e.target.value
                  ? Number(e.target.value)
                  : undefined;
                setFilters({ ...filters, bookingId: value });
              }}
              style={{ marginBottom: 8 }}
            />
          </Col>
          <Col xs={24} md={4}>
            <Select
              placeholder="Company"
              style={{ width: "100%", marginBottom: 8 }}
              allowClear
              showSearch
              optionFilterProp="children"
              filterOption={(input, option) => {
                const childText = option?.children?.toString() || "";
                return (
                  childText.toLowerCase().indexOf(input.toLowerCase()) >= 0
                );
              }}
              value={filters.companyId}
              onChange={(value) => setFilters({ ...filters, companyId: value })}
            >
              {companies.map((company) => (
                <Option key={company.id} value={company.id}>
                  {company.name}
                </Option>
              ))}
            </Select>
          </Col>
          <Col xs={24} md={4}>
            <Select
              placeholder="Assigned To"
              style={{ width: "100%", marginBottom: 8 }}
              allowClear
              value={filters.adminUserId}
              onChange={(value) =>
                setFilters({ ...filters, adminUserId: value })
              }
            >
              {adminUsers.map((admin) => (
                <Option key={admin.user_id} value={admin.user_id}>
                  {admin.name || "—"}
                </Option>
              ))}
            </Select>
          </Col>
          <Col xs={24} md={4}>
            <Select
              placeholder="Status"
              style={{ width: "100%", marginBottom: 8 }}
              allowClear
              value={filters.completed}
              onChange={(value) => setFilters({ ...filters, completed: value })}
            >
              <Option value={false}>Pending</Option>
              <Option value={true}>Completed</Option>
            </Select>
          </Col>
          <Col xs={24} md={4}>
            <RangePicker
              style={{ width: "100%", marginBottom: 8 }}
              onChange={(dates) =>
                setFilters({ ...filters, date: dates as [Dayjs, Dayjs] })
              }
              value={filters.date}
            />
          </Col>
        </Row>
        <Row>
          <Col>
            {user && (
              <Button
                type={filters.adminUserId === user.id ? "primary" : "default"}
                icon={<UserOutlined />}
                onClick={() =>
                  setFilters({
                    ...filters,
                    adminUserId:
                      filters.adminUserId === user.id ? undefined : user.id,
                  })
                }
                style={{ marginRight: 8 }}
              >
                My Tasks
              </Button>
            )}
            <Button onClick={resetFilters} style={{ marginRight: 8 }}>
              Reset Filters
            </Button>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => {
                setModalTitle("Add Task");
                setEditingTask(null);
                form.resetFields();
                fetchBookings();
                setModalVisible(true);
              }}
            >
              Add Task
            </Button>
          </Col>
        </Row>
      </div>

      {/* Tasks Table */}
      <Table
        dataSource={tasks}
        columns={columns}
        rowKey="id"
        loading={loading}
        expandable={{
          expandedRowRender: (record) => (
            <p style={{ margin: 0 }}>
              {record.description || "No description provided"}
            </p>
          ),
          rowExpandable: (record) => Boolean(record.description),
        }}
        pagination={{
          pageSize: 10,
          showSizeChanger: true,
          pageSizeOptions: ["10", "20", "50"],
        }}
      />

      {/* Add/Edit Task Modal */}
      <Modal
        title={modalTitle}
        open={modalVisible}
        onCancel={() => {
          setModalVisible(false);
          setEditingTask(null);
          form.resetFields();
        }}
        footer={[
          <Button
            key="cancel"
            onClick={() => {
              setModalVisible(false);
              setEditingTask(null);
              form.resetFields();
            }}
          >
            Cancel
          </Button>,
          <Button key="submit" type="primary" onClick={() => form.submit()}>
            Save
          </Button>,
        ]}
        width={700}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleFormSubmit}
          initialValues={{ completed: false }}
        >
          <Form.Item
            name="name"
            label="Task Name"
            rules={[{ required: true, message: "Please enter a task name" }]}
          >
            <Input placeholder="Enter task name" />
          </Form.Item>

          <Form.Item name="description" label="Description">
            <TextArea rows={4} placeholder="Enter task description" />
          </Form.Item>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="company_id" label="Related Company">
                <Select
                  placeholder="Select a company"
                  allowClear
                  showSearch
                  optionFilterProp="children"
                  filterOption={(input, option) => {
                    const childText = option?.children?.toString() || "";
                    return (
                      childText.toLowerCase().indexOf(input.toLowerCase()) >= 0
                    );
                  }}
                  onChange={(value) => {
                    // When company changes, reset booking field and refresh bookings
                    form.setFieldValue("booking_id", undefined);
                    if (value) {
                      fetchBookings(value as number);
                    } else {
                      fetchBookings();
                    }
                  }}
                >
                  {companies.map((company) => (
                    <Option key={company.id} value={company.id}>
                      {company.name}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="booking_id" label="Related Booking">
                <Select
                  placeholder="Select a booking"
                  allowClear
                  showSearch
                  optionFilterProp="children"
                  filterOption={(input, option) => {
                    const childText = option?.children?.toString() || "";
                    return (
                      childText.toLowerCase().indexOf(input.toLowerCase()) >= 0
                    );
                  }}
                >
                  {bookings.map((booking) => (
                    <Option key={booking.id} value={booking.id}>
                      {formatBookingId(booking)} -{" "}
                      {booking.reference || "No reference"}
                      {booking.company_name ? ` (${booking.company_name})` : ""}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="admin_user_id" label="Assigned To">
                <Select
                  placeholder="Select an admin"
                  allowClear
                  showSearch
                  optionFilterProp="children"
                  filterOption={(input, option) => {
                    const childText = option?.children?.toString() || "";
                    return (
                      childText.toLowerCase().indexOf(input.toLowerCase()) >= 0
                    );
                  }}
                >
                  {adminUsers.map((admin) => (
                    <Option key={admin.user_id} value={admin.user_id}>
                      {admin.name || "—"}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="date" label="Due Date">
                <DatePicker style={{ width: "100%" }} format="YYYY-MM-DD" />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item name="completed" valuePropName="checked">
            <Checkbox>Mark as completed</Checkbox>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default TasksAdmin;

// Helper component for statistics display
const Statistic = ({
  title,
  value,
  valueStyle,
  prefix,
}: {
  title: string;
  value: number;
  valueStyle?: React.CSSProperties;
  prefix?: React.ReactNode;
}) => (
  <div>
    <Text type="secondary">{title}</Text>
    <div style={{ display: "flex", alignItems: "center", marginTop: 4 }}>
      {prefix}
      <Text style={{ fontSize: 24, ...valueStyle }}>{value}</Text>
    </div>
  </div>
);
